import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import followService from "../../services/followService";

/**
 * Carte d'utilisateur pour la communauté
 * Affiche les informations et permet de suivre/ne plus suivre
 */
const UserCard = ({ user, currentUser, onFollowChange }) => {
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    try {
      if (isFollowing) {
        await followService.unfollowUser(user.id);
        setIsFollowing(false);
      } else {
        await followService.followUser(user.id);
        setIsFollowing(true);
      }
      if (onFollowChange) {
        onFollowChange();
      }
    } catch (error) {
      console.error("Erreur lors du suivi:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.username[0].toUpperCase();
  };

  const getBadgeColor = (level) => {
    const colors = {
      bronze: "from-orange-400 to-orange-600",
      silver: "from-gray-300 to-gray-500",
      gold: "from-yellow-400 to-yellow-600",
      platinum: "from-cyan-400 to-blue-600",
      diamond: "from-purple-400 to-pink-600",
    };
    return (
      colors[user.cooking_level?.toLowerCase()] || "from-gray-400 to-gray-600"
    );
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 p-4 sm:p-6 border border-white/20">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          {/* Avatar - plus petit sur mobile */}
          <div
            className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${getBadgeColor()} rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-base sm:text-xl font-bold shadow-md flex-shrink-0`}
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.username}
                className="w-full h-full rounded-xl sm:rounded-2xl object-cover"
              />
            ) : (
              getInitials()
            )}
          </div>

          {/* Informations utilisateur */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-white truncate">
              {user.first_name && user.last_name
                ? `${user.first_name} ${user.last_name}`
                : user.username}
            </h3>
            <p className="text-xs sm:text-sm text-white/80 truncate">@{user.username}</p>

            {user.cooking_level && (
              <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-500/30 text-amber-200">
                {user.cooking_level}
              </div>
            )}
          </div>
        </div>

        {/* Bouton suivre - pleine largeur sur mobile */}
        {currentUser && currentUser.id !== user.id && (
          <button
            onClick={handleFollow}
            disabled={loading}
            className={`w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all duration-200 touch-manipulation flex-shrink-0 ${
              isFollowing
                ? "bg-white/10 text-white border border-white/20 hover:bg-white/20"
                : "bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-md"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? "..." : isFollowing ? "Abonné" : "Suivre"}
          </button>
        )}
      </div>

      {/* Statistiques - plus compact sur mobile */}
      <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-4">
        <div className="text-center py-1 sm:py-0">
          <div className="text-base sm:text-xl font-bold text-orange-400 tabular-nums">
            {user.total_recipes || 0}
          </div>
          <div className="text-[10px] sm:text-xs text-white/90 font-medium">Recettes</div>
        </div>
        <div className="text-center py-1 sm:py-0">
          <div className="text-base sm:text-xl font-bold text-amber-400 tabular-nums">
            {user.total_followers || 0}
          </div>
          <div className="text-[10px] sm:text-xs text-white/90 font-medium">Abonnés</div>
        </div>
        <div className="text-center py-1 sm:py-0">
          <div className="text-base sm:text-xl font-bold text-orange-300 tabular-nums">
            {user.total_badges || 0}
          </div>
          <div className="text-[10px] sm:text-xs text-white/90 font-medium">Badges</div>
        </div>
      </div>
    </div>
  );
};

export default UserCard;
